import React from 'react';

/**
 * SkeletonLogin - Loading skeleton for public pages
 * Prevents layout shift during code splitting / lazy loading
 * Matches LoginLayout dimensions and structure
 */
const SkeletonLogin: React.FC = () => {
    return (
        <div
            className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden font-sans"
            style={{
                background: 'linear-gradient(135deg, #FAFBFC 0%, #DCEEF3 100%)',
            }}
        >
            {/* Ambient Background (static, no animation for performance) */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                <div
                    className="absolute top-[-15%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[100px]"
                    style={{
                        background: 'radial-gradient(circle, rgba(18, 55, 64, 0.06) 0%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[100px]"
                    style={{
                        background: 'radial-gradient(circle, rgba(84, 154, 171, 0.12) 0%, transparent 70%)',
                    }}
                />
            </div>

            {/* Glassmorphism Container Skeleton */}
            <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div
                    className="rounded-3xl p-8 md:p-12 lg:p-16"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.65) 100%)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                >
                    <div className="flex flex-col items-center text-center animate-pulse">
                        {/* Logo Skeleton */}
                        <div className="w-20 h-20 bg-gray-200/50 rounded-2xl mb-8" />

                        {/* Title Skeleton */}
                        <div className="w-3/4 h-12 bg-gray-200/50 rounded-xl mb-4" />
                        <div className="w-2/3 h-6 bg-gray-200/50 rounded-lg mb-12" />

                        {/* Value Props Skeleton */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 w-full max-w-4xl">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="p-6 rounded-2xl bg-white/30 border border-white/20"
                                >
                                    <div className="w-12 h-12 bg-gray-200/50 rounded-lg mb-3 mx-auto" />
                                    <div className="w-3/4 h-5 bg-gray-200/50 rounded mb-2 mx-auto" />
                                    <div className="w-full h-4 bg-gray-200/50 rounded mx-auto" />
                                </div>
                            ))}
                        </div>

                        {/* CTA Buttons Skeleton */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-16">
                            <div className="w-40 h-14 bg-primary/30 rounded-xl" />
                            <div className="w-40 h-14 bg-gray-200/50 rounded-xl" />
                        </div>

                        {/* Footer Skeleton */}
                        <div className="w-full pt-8 border-t border-gray-200/30">
                            <div className="flex justify-center gap-4 mb-4">
                                <div className="w-32 h-4 bg-gray-200/50 rounded" />
                                <div className="w-32 h-4 bg-gray-200/50 rounded" />
                                <div className="w-32 h-4 bg-gray-200/50 rounded" />
                            </div>
                            <div className="w-48 h-4 bg-gray-200/50 rounded mx-auto" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkeletonLogin;
